/**
 * PB (Personal Best) Detection Utilities
 *
 * Pure functions for detecting personal bests by comparing record values.
 * Supports higher-is-better (weight, reps, rounds, distance, calories)
 * and lower-is-better (time) comparison directions.
 */

/**
 * Parse a time string to total seconds.
 * @param {string} timeStr - Format "mm:ss" or "hh:mm:ss"
 * @returns {number} Total seconds, or NaN if invalid
 */
export function parseTimeString(timeStr) {
	if (!timeStr || typeof timeStr !== "string") return NaN;
	const parts = timeStr.trim().split(":").map(Number);
	if (parts.some(isNaN)) return NaN;
	if (parts.length === 2) {
		// mm:ss
		return parts[0] * 60 + parts[1];
	}
	if (parts.length === 3) {
		// hh:mm:ss
		return parts[0] * 3600 + parts[1] * 60 + parts[2];
	}
	return NaN;
}

/**
 * Format total seconds as "mm:ss" or "hh:mm:ss".
 * @param {number} totalSeconds
 * @returns {string}
 */
export function formatTimeString(totalSeconds) {
	if (totalSeconds < 0 || !Number.isFinite(totalSeconds)) return "0:00";
	const hrs = Math.floor(totalSeconds / 3600);
	const mins = Math.floor((totalSeconds % 3600) / 60);
	const secs = Math.floor(totalSeconds % 60);
	const pad = (n) => n.toString().padStart(2, "0");
	if (hrs > 0) {
		return `${hrs}:${pad(mins)}:${pad(secs)}`;
	}
	return `${mins}:${pad(secs)}`;
}

/**
 * Determine if a record value is a personal best.
 * @param {object} newRecord - { countType, value }
 * @param {Array} existingRecords - All records for same user/movement with id, countType, value
 * @param {string|null} excludeRecordId - Record ID to exclude (for updates)
 * @returns {boolean} true if the new value is a PB
 */
export function detectPB(newRecord, existingRecords, excludeRecordId = null) {
	const { countType, value } = newRecord;

	// Filter to same countType and exclude the record being updated
	const comparables = existingRecords.filter(
		(r) => r.countType === countType && r.id !== excludeRecordId,
	);

	// First record for this movement+countType is always a PB
	if (comparables.length === 0) return true;

	if (countType === "time") {
		const newSeconds = parseTimeString(value);
		if (isNaN(newSeconds)) return false;
		const previousSeconds = comparables
			.map((r) => parseTimeString(r.value))
			.filter((s) => !isNaN(s));
		if (previousSeconds.length === 0) return true;
		return newSeconds < Math.min(...previousSeconds);
	}

	// Higher is better: weight, reps, rounds, distance, calories
	const newNum = parseFloat(value);
	if (isNaN(newNum)) return false;
	const previousValues = comparables
		.map((r) => parseFloat(r.value))
		.filter((v) => !isNaN(v));
	if (previousValues.length === 0) return true;
	return newNum > Math.max(...previousValues);
}
